/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import ProductDetail from '@/components/ProductDetail';
import { getGlobalConfigFromCache, getFeatureFlag } from '@/features/globalConfig';
import { getHeaderDataFromCache } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { prefetchPreFooterData } from '@/features/prefooter/api/getPreFooterData';
import { getPageDependentPrefooterParams } from '@/features/prefooter/utils/getPageDependentPrefooterParams';
import {
  prefetchProductDetails,
  prefetchProductDetailsACES,
  getProductDetailsFromCache,
  getProductDetailsFromCacheACES,
} from '@/features/product/api/getProductDetails';
import { prefetchProductSkuDeals } from '@/features/product/api/getProductDeals';
import { prefetchProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { prefetchGetProductSkuPrice } from '@/features/product/api/getProductSkuPrice';
import {
  getProductComparisonFromCache,
  prefetchProductComparison,
} from '@/features/product/api/getProductComparison';
import { hasCMSPremiumTable } from '@/features/pdp';
import { prefetchGcpProductVideos } from '@/features/pdp/api/getGcpProductVideos';
import {
  prefetchContentStackDefaultPDPContent,
  getContentStackDefaultPDPContentFromCache,
} from '@/features/pdp/api/getContentStackDefaultProductContent';
import {
  prefetchContentStackPremiumPDPContent,
  getContentStackPremiumPDPContentFromCache,
} from '@/features/pdp/api/getContentStackPremiumProductContent';
import { prefetchContentStackEnhancedPDPContent } from '@/features/pdp/api/getContentStackEnhancedProductContent';
import type { PageContextType } from '@/next-types';
import { isEmptyObject } from '@/utils/common';
import { configureSharedPageData } from '@/utils/configureSharedPageData.server';
import { getPathnameFromAsPath, parseUrl } from '@/utils/urlHelpers';
import { getDuplicateSkuQueryParams } from '@/utils/getDuplicateSkuQueryParams';
import { dehydrate } from '@tanstack/react-query';
import { ProductListProvider } from '@/features/shelf/context/ProductListProvider';
import { prefetchDynamicLinkMappings } from '@/features/dlm/api/getDynamicLinkMappings';
import environmentConfig from '@/config/environment';
import { getDevice } from '@/utils/getDeviceType.server';
import { SisterStoreProvider } from '@/features/fulfillment/context/SisterStoreProvider';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { countryCodes } from '@/constants/locale';
import { prefetchSylvaniaVideos } from '@/features/product/api/getSylvaniaVideos';
import { prefetchProductVehicleImage } from '@/features/pdp/api/getGcpProductVehicleImage';
import { addVehicleFromQueryParams } from '@/features/sharableLink/utils/addVehicleFromQueryParams';
import { ReviewFiltersProvider } from '@/features/customReviews/context/ReviewFiltersProvider';
import { type ProductDetailsOptionsACES, type ProductData } from '@/features/product';
import { getAcesFlag } from '@/features/ymme/utils/acesFlags';
import type { ProductModel, ProductModelACES } from '@/api/types/products-types';
import logger from '@/utils/logger';
import { prefetchProductReviews } from '@/features/reviews/api/getProductReviews';
import { prefetchAuthenticDiscoveries } from '@/features/customReviews/api/getAuthenticDiscoveries';
import { getBaseSchemaUrl } from '@/utils/common';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { prefetchCatalogConfigs } from '@/features/common/api/getCatalogConfig';
import { type CatalogConfigsOptions } from '@/features/common';
import { type PDPComparisonParams } from '@/features/product';
import { prefetchReviewsInfinite } from '@/features/customReviews/api/getReviews';
import { DEFAULT_SORT_ATTRIBUTE } from '@/features/customReviews/constants/defaultSortAttribute';
import { prefetchReviewSummary } from '@/features/customReviews/api/getReviewSummary';
import { prefetchReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { prefetchReviewsWithImages } from '@/features/customReviews/hooks/useProductReviewImages';
import type { ProductReviewsOptions } from '../../features/reviews/interface';
import { hasBVReviewsSection } from '@/features/pdp/utils/hasBVReviewsSection';
import { getOMSFlags } from '@/features/product/utils/getOMSFlags';
import { getGCPPriceFlags } from '@/features/product/utils/getGCPPriceFlags';
import { prefetchAcesSylvaniaVideos } from '@/features/product/api/getAcesSylvaniaVideo';
import { prefetchAcesProductVehicleImage } from '@/features/pdp/api/getAcesGcpProductVehicleImage';
import { isServer } from '@/utils/isServer';
import { getGlobalStateQueryData } from '@/hooks/useGlobalState';
import { getForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

export const config = {
  unstable_JsPreload: false,
};

function ProductDetailsPage() {
  return (
    <SisterStoreProvider>
      <ReviewFiltersProvider>
        <ProductDetail />
      </ReviewFiltersProvider>
    </SisterStoreProvider>
  );
}

ProductDetailsPage.getInitialProps = async (context: PageContextType) => {
  const { req, query, res, asPath } = context;

  if (isServer) {
    const { slug } = query;
    const device = getDevice(req);

    if (!Array.isArray(slug)) {
      throw new Error('The slug for ProductDetails must be an array');
    }

    const { axios, queryClient, initialAppState } = await configureSharedPageData(context);
    const { locale } = initialAppState;
    const { acesEnabled, acesTransitionPhase } = getAcesFlag(queryClient);
    const isUsOrMxSite = locale === countryCodes.us || locale === countryCodes.mx;

    if (query.vehicleId || query.year) {
      await addVehicleFromQueryParams(
        queryClient,
        query,
        locale,
        axios,
        asPath,
        { acesEnabled, acesTransitionPhase },
        {
          pageName: 'PDP',
        },
      );
    }

    // After updating vehicles, header data is refetched, so we should read it after
    const headerData = getHeaderDataFromCache(queryClient);
    const segments = headerData?.segments?.toSorted() ?? [];
    const preferredVehicle = getPreferredVehicle(headerData);
    const {
      catalogVehicleId = '0',
      year,
      makeId,
      modelId,
      vehicleTypeId,
      vehicleQuestionsIds,
    } = preferredVehicle || {};
    const vehicleQuestions = extractVehicleQuestions(vehicleQuestionsIds, true) as string;
    const storeNumber = headerData?.storeNumber ?? '';
    const globalConfig = getGlobalConfigFromCache(queryClient);
    const seoUrl = `/${slug.join('/')}`;

    const productDetailsOptions = {
      vehicleId: Number(catalogVehicleId),
      locale,
      lineCode: undefined,
      productDetailsPageUrl: seoUrl,
      userSegment: undefined,
      partNumber: undefined,
      itemIds: undefined,
      storeId: storeNumber ? Number(storeNumber) : undefined,
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
    };

    const duplicateSkuParams = getDuplicateSkuQueryParams(query);

    const authenticDiscoveryEnabled =
      getFeatureFlag(globalConfig, 'AUTHENTIC_DISCOVERY_ENABLED') === 'true';
    const isV2ProductsEnabled = getFeatureFlag(globalConfig, 'PDP_ELASTIC_ENABLED') === 'true';

    const productDetailsOptionsACES: ProductDetailsOptionsACES = {
      country: getCountryFromLocale(initialAppState.locale),
      ...(storeNumber && { storeId: Number(storeNumber) }),
      productDetailsPageUrl: seoUrl,
      year: Number(year) || undefined,
      makeId: Number(makeId) || undefined,
      modelId: Number(modelId) || undefined,
      vehicleTypeId: Number(vehicleTypeId) || undefined,
      partGroupId: duplicateSkuParams.partGroupId,
      brandId: duplicateSkuParams.brandId,
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
      positionId: duplicateSkuParams.positionId,
      uniqueId: duplicateSkuParams.uniqueId,
      ...(vehicleQuestions.length > 0 && { vehicleQuestions }),
      isV2ProductsEnabled,
    };

    let productDetails: ProductData | undefined;
    let product: ProductModel | ProductModelACES | undefined;
    try {
      if (acesEnabled) {
        await prefetchProductDetailsACES(queryClient, productDetailsOptionsACES, axios);
        productDetails = getProductDetailsFromCacheACES(queryClient, productDetailsOptionsACES);
      } else {
        await prefetchProductDetails(queryClient, productDetailsOptions, axios);
        productDetails = getProductDetailsFromCache(queryClient, productDetailsOptions);
      }
    } catch {
      logger.error({
        message: 'Error fetching product details',
        meta: {
          url: asPath,
        },
      });
    } finally {
      product = acesEnabled
        ? (productDetails?.product as ProductModelACES)
        : (productDetails?.product as ProductModel);
    }

    const country = getCountryFromLocale(locale);
    const skuNumber = productDetails?.product?.itemId ?? '';

    const appData = getGlobalStateQueryData('appData', queryClient) ?? { deviceType: '' };
    const isBot = appData.deviceType === 'bot';

    const isSuperSkusEnabled = getFeatureFlag(globalConfig, 'IS_SUPER_SKUS_ENABLED') === 'true';

    const isSuperSkuPage =
      isSuperSkusEnabled &&
      !!productDetails?.product &&
      'choices' in productDetails.product &&
      'availableOptions' in productDetails.product;

    const superSkuRedirectPath: string | undefined = undefined;

    const redirectPath = productDetails?.redirectURL ?? superSkuRedirectPath;

    if (redirectPath) {
      const parsedPath = parseUrl(redirectPath);
      let sanitizedPath = redirectPath;

      if (!isEmptyObject(parsedPath?.query)) {
        sanitizedPath = parsedPath.url;
      }

      // Do not send full path with query params for bots: We explicitly append recsPerPage: 24 for bots when constructing the completeURL for the ATG call in this file.
      // However, even though we are requesting 24 recsPerPage, SEO does not want this query param to show up in the redirect url since bots are technically not be able to change recsPerPage.
      const path = isBot ? sanitizedPath : redirectPath;

      if (path !== undefined) {
        res?.writeHead(301, { Location: path }).end();
        return {
          initialAppState,
          dehydratedState: dehydrate(queryClient),
        };
      }
    }

    let skuComparisonPromise: Promise<void> | undefined;
    let cmsPremiumContentPromise: Promise<void> | undefined;
    let dynamicLinksPromise: Promise<void> | undefined;
    let sylvaniaVideosPromise: Promise<void> | undefined;
    let productVehiclePromise: Promise<void> | undefined;
    let superSkuPricingPromise: Promise<void> | undefined;
    let superSkuDealsPromise: Promise<void> | undefined;

    const productId = acesEnabled
      ? (productDetails?.product?.partGroupId ?? productDetails?.product?.ecommProductId)
      : productDetails?.product?.ecommProductId;
    const catalogConfigsOptions: CatalogConfigsOptions = {
      itemId: productId ?? '',
      itemType: 'product',
      country,
    };

    const catalogConfigsPromise: Promise<void> | undefined = prefetchCatalogConfigs(
      catalogConfigsOptions,
      queryClient,
      axios,
    );

    // Prefetch super SKU variant pricing for bots to ensure pricing appears in SSR HTML
    if (isSuperSkuPage && isBot && productDetails?.product?.choices) {
      const allSuperSkuIds = productDetails.product.choices
        .map((choice) => choice.choice?.itemId)
        .filter((id): id is string => Boolean(id));

      if (allSuperSkuIds.length > 0) {
        const firstPageSkuIds = allSuperSkuIds.slice(0, 12);

        superSkuPricingPromise = prefetchGetProductSkuPrice(
          queryClient,
          { skuNumbers: firstPageSkuIds, storeNumber },
          axios,
        );

        if (getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true') {
          superSkuDealsPromise = prefetchProductSkuDeals(
            {
              acesEnabled,
              preferredVehicle,
              skuIds: firstPageSkuIds,
              storeNumber,
              vehicleId: catalogVehicleId,
            },
            queryClient,
            axios,
          );
        }
      }
    }

    const taxonomyPath = productDetails?.product?.cmsContentPath ?? '';
    const brandName =
      productDetails?.product?.subBrandName ?? productDetails?.product?.brandName ?? '';

    const productContentsPromise = prefetchContentStackDefaultPDPContent(
      queryClient,
      taxonomyPath,
      context,
      segments,
    );

    const isEnhancedPdpEnabled =
      getForteDecisionFlagWithTrueDefault(queryClient, 'enhancedPdpEnabled') ?? false;

    const enhancedPDPContentPromise = isEnhancedPdpEnabled
      ? prefetchContentStackEnhancedPDPContent(
          queryClient,
          taxonomyPath.replace(/\/b\/.*$/, ''),
          context,
          segments,
          brandName,
          productDetails?.product?.itemId,
        )
      : undefined;

    productContentsPromise
      .then(() => {
        const pdpDefault = getContentStackDefaultPDPContentFromCache(
          queryClient,
          taxonomyPath,
          segments,
        );

        if (
          !isEnhancedPdpEnabled &&
          taxonomyPath &&
          productDetails?.product?.brandName &&
          ((pdpDefault?.premiumBrands?.brands ?? []) as string[]).includes(
            productDetails.product.brandName,
          )
        ) {
          cmsPremiumContentPromise = prefetchContentStackPremiumPDPContent(
            queryClient,
            taxonomyPath,
            context,
            segments,
            brandName,
          );
        }
      })
      .catch(() => {});

    if (
      Boolean(productDetails?.product?.vehicleFitFlag) &&
      Boolean(catalogVehicleId) &&
      catalogVehicleId !== '0' &&
      productDetails?.product?.itemId &&
      productDetails?.product?.lineCode === 'SYL' &&
      getFeatureFlag(globalConfig, 'IS_YMME_INSTALL_VIDEOS_ENABLED') === 'true'
    ) {
      const sylvaniaVideosParams = {
        productId: productDetails.product.itemId,
        locale,
        vehicleId: catalogVehicleId,
      };
      sylvaniaVideosPromise = acesEnabled
        ? prefetchAcesSylvaniaVideos(axios, queryClient, {
            skuId: productDetails.product.itemId,
            locale,
          })
        : prefetchSylvaniaVideos(axios, queryClient, sylvaniaVideosParams);
    }

    if (
      getFeatureFlag(globalConfig, 'IS_PRODUCT_VEHICLE_IMAGE_ENABLED') === 'true' &&
      catalogVehicleId !== '0' &&
      Boolean(catalogVehicleId) &&
      'originalPartTypeId' in product
    ) {
      const productVehicleImageParams = {
        vehicleId: catalogVehicleId,
        partCode: product.originalPartTypeId || '',
        country: country,
      };

      productVehiclePromise = acesEnabled
        ? prefetchAcesProductVehicleImage(axios, queryClient, {
            partCode: product.partGroupId ?? '',
            country,
            year: year ?? '',
            makeId: makeId ? Number(makeId) : undefined,
            modelId: modelId ? Number(modelId) : undefined,
          })
        : prefetchProductVehicleImage(axios, queryClient, productVehicleImageParams);
    }

    let reviewsPromise: Promise<void> | undefined;
    let reviewsWithImagesPromise: Promise<void> | undefined;
    let reviewsSummaryPromise: Promise<void> | undefined;
    let productReviewsStatisticsPromise: Promise<void> | undefined;
    let seoReviewsPromise: Promise<void> | undefined;
    let authenticDiscoveriesPromise: Promise<void> | undefined;
    if (skuNumber) {
      productReviewsStatisticsPromise = skuNumber
        ? prefetchReviewStatistics({ skuNumbers: [skuNumber], locale }, queryClient, axios)
        : undefined;
    }

    if (skuNumber && isBot) {
      await productContentsPromise;
      await cmsPremiumContentPromise;

      const pdpContent =
        getContentStackPremiumPDPContentFromCache(queryClient, taxonomyPath, segments, brandName) ??
        getContentStackDefaultPDPContentFromCache(queryClient, taxonomyPath, segments);

      const isReviewsSectionDisplayed = pdpContent?.pageBody?.some(hasBVReviewsSection) ?? false;

      if (isReviewsSectionDisplayed) {
        reviewsPromise = prefetchReviewsInfinite(
          {
            skuNumber,
            sortAttribute: DEFAULT_SORT_ATTRIBUTE,
            limit: 20,
            locale,
          },
          queryClient,
          axios,
        );

        reviewsWithImagesPromise = prefetchReviewsWithImages(
          { skuNumber, locale },
          queryClient,
          axios,
        );

        reviewsSummaryPromise = prefetchReviewSummary({ skuNumber, locale }, queryClient, axios);

        if (authenticDiscoveryEnabled) {
          const canonical = `${getBaseSchemaUrl(locale)}${seoUrl}`;
          authenticDiscoveriesPromise = prefetchAuthenticDiscoveries(
            { skuId: skuNumber, locale: locale.replace('-', '_'), canonical },
            queryClient,
            axios,
          );
        } else if (globalConfig?.PRP_ENABLED === 'true') {
          const productReviewsOptions: ProductReviewsOptions = {
            country: getCountryFromLocale(initialAppState.locale),
            customerType: 'B2C',
            skuId: skuNumber,
            pageNumber: 0,
          };
          seoReviewsPromise = prefetchProductReviews(productReviewsOptions, queryClient, axios);
        }
      }
    }

    const { useOMSInventoryQohService } = getOMSFlags(queryClient);
    const { useGCPPriceService } = getGCPPriceFlags(queryClient);

    if (
      device.type === 'bot' &&
      getFeatureFlag(globalConfig, 'PDP_COMPARISON_ENABLED') === 'true' &&
      productDetails?.product?.brandName &&
      productId
    ) {
      const comparisonCurrentSkuId = productDetails.product.itemId;
      const productComparisonParams: PDPComparisonParams = {
        parentBrandName: productDetails.product.brandName,
        subBrandName: productDetails.product.subBrandName,
        productId,
        skuId: comparisonCurrentSkuId,
        storeNumber: storeNumber || undefined,
        vehicleId: catalogVehicleId,
        country: getCountryFromLocale(initialAppState.locale),
        year: preferredVehicle?.year,
        makeId: preferredVehicle?.makeId,
        modelId: preferredVehicle?.modelId,
        vehicleTypeId: preferredVehicle?.vehicleTypeId,
        vehicleQuestions: preferredVehicle?.vehicleQuestionsIds,
        acesEnabled,
      };

      await productContentsPromise;
      await cmsPremiumContentPromise;
      const premiumPDPContent = getContentStackPremiumPDPContentFromCache(
        queryClient,
        taxonomyPath,
        segments,
        brandName,
      );
      if (!premiumPDPContent || !premiumPDPContent.pageBody?.some(hasCMSPremiumTable)) {
        skuComparisonPromise = (async () => {
          await prefetchProductComparison(productComparisonParams, queryClient, axios);

          const productComparisonData = getProductComparisonFromCache(
            productComparisonParams,
            queryClient,
          );
          const comparisonSkuIds = (productComparisonData?.skus ?? []).flatMap((sku) =>
            sku.skuId ? [sku.skuId] : [],
          );
          const isNewDealsApiEnabled =
            getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true';

          await Promise.all([
            comparisonSkuIds.length
              ? prefetchProductSkuDetails(axios, queryClient, {
                  skuIds: comparisonSkuIds,
                  storeNumber,
                  vehicleId: catalogVehicleId,
                  isNewDealsApiEnabled:
                    getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true',
                  useOMSInventoryQohService,
                  useGCPPriceService,
                  acesEnabled,
                })
              : undefined,
            comparisonSkuIds.length && isUsOrMxSite && isNewDealsApiEnabled
              ? prefetchProductSkuDeals(
                  {
                    acesEnabled,
                    preferredVehicle,
                    skuIds: comparisonSkuIds,
                    storeNumber,
                    vehicleId: catalogVehicleId,
                  },
                  queryClient,
                  axios,
                )
              : undefined,
            comparisonCurrentSkuId && isUsOrMxSite && isNewDealsApiEnabled
              ? prefetchProductSkuDeals(
                  {
                    acesEnabled,
                    preferredVehicle,
                    skuIds: [comparisonCurrentSkuId],
                    storeNumber,
                    vehicleId: catalogVehicleId,
                  },
                  queryClient,
                  axios,
                )
              : undefined,
          ]);
        })();
      }
    }

    if (
      device.type === 'bot' &&
      productDetails?.product?.itemId &&
      getFeatureFlag(globalConfig, 'DLM_PDP_ENABLED') === 'true' &&
      catalogVehicleId === '0'
    ) {
      dynamicLinksPromise = prefetchDynamicLinkMappings(
        {
          type: 'generic',
          country: getCountryFromLocale(locale),
          seoRepositoryId: productDetails.product.itemId,
        },
        queryClient,
        axios,
      );
    }

    const skuDetailsPromise = productDetails?.product?.itemId
      ? prefetchProductSkuDetails(axios, queryClient, {
          skuIds: [productDetails.product.itemId],
          storeNumber,
          vehicleId: catalogVehicleId,
          isNewDealsApiEnabled: getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true',
          useOMSInventoryQohService,
          useGCPPriceService,
          acesEnabled,
        })
      : undefined;

    await Promise.all([
      catalogConfigsPromise,
      reviewsPromise,
      reviewsWithImagesPromise,
      reviewsSummaryPromise,
      productReviewsStatisticsPromise,
      seoReviewsPromise,
      productContentsPromise,
      cmsPremiumContentPromise,
      enhancedPDPContentPromise,
      skuDetailsPromise,
      productDetails?.product?.itemId &&
        isUsOrMxSite &&
        prefetchGcpProductVideos({
          queryClient,
          country,
          skuId: productDetails.product.itemId,
          axiosInstance: axios,
        }),
      prefetchPreFooterData(
        queryClient,
        {
          locale: initialAppState.locale,
          seourl: getPathnameFromAsPath(asPath).replace(/(\/bn\/|\/b\/).*/, ''),
          vehicleOptions: acesEnabled
            ? {
                vehicleId: undefined,
                vehicleTypeId: preferredVehicle?.vehicleTypeId,
                makeId: preferredVehicle?.makeId,
                modelId: preferredVehicle?.modelId,
                year: preferredVehicle?.year,
                vehicleQuestions: preferredVehicle?.vehicleQuestionsIds,
              }
            : {
                vehicleId: catalogVehicleId,
                vehicleTypeId: undefined,
                makeId: undefined,
                modelId: undefined,
                year: undefined,
                vehicleQuestions: undefined,
              },
          shouldCallACES: acesEnabled,
          ...getPageDependentPrefooterParams({
            isPDP: true,
            pageTypeData: undefined,
            productDetails,
            acesEnabled,
          }),
        },
        axios,
      ),
      productDetails?.product?.itemId &&
        getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true' &&
        prefetchProductSkuDeals(
          {
            acesEnabled,
            preferredVehicle,
            skuIds: [productDetails.product.itemId],
            storeNumber,
            vehicleId: catalogVehicleId,
          },
          queryClient,
          axios,
        ),
      skuComparisonPromise,
      dynamicLinksPromise,
      sylvaniaVideosPromise,
      productVehiclePromise,
      superSkuPricingPromise,
      superSkuDealsPromise,
      authenticDiscoveriesPromise,
    ]);

    return {
      initialAppState,
      dehydratedState: dehydrate(queryClient),
    };
  }

  return {};
};

ProductDetailsPage.getLayout = (page: React.ReactNode) => {
  return <ProductListProvider>{page}</ProductListProvider>;
};

export default ProductDetailsPage;
